import { computed, inject, Injectable, signal, Type, untracked } from '@angular/core';
import { API_BASE_URL } from '@app/core/constants';
import { HttpService } from '@app/modules/core/services/http.service';
import { DatasetSubtype, DatasetType, Document, Filter, FilterGroup, FilterGroupType, RuleOperator } from '@app/modules/core/store/models';
import { RatingArea, RatingScale, RatingScaleLevel } from '@app/modules/management/store/ratings/ratings.model';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as countries from 'i18n-iso-countries';
import { v4 as uuidv4 } from 'uuid';
import { Folder, LayerTypes, SectionType, selectDatasetsForSelect } from '../../store/cop';
import { HorizonCountryCodeByInternalIdMap } from './horizon-country-code-by-internal-id-map';
import { SvgComponent } from '@app/shared';
import { HeartSVGComponent } from '@app/shared/components/svgs/heart-svg.component';

export enum HorizonRatings {
  Extreme = 'extreme',
  High = 'high',
  Moderate = 'moderate',
  Low = 'low',
  Minimal = 'minimal',
  Unrated = 'unrated'
}

export const HorizonRatingColors = {
  [HorizonRatings.Extreme]: '#e03131',
  [HorizonRatings.High]: '#fd7e14',
  [HorizonRatings.Moderate]: '#fcc419',
  [HorizonRatings.Low]: '#82c91e',
  [HorizonRatings.Minimal]: '#2b8a3e',
  [HorizonRatings.Unrated]: '#d6d5dd'
};

export const HorizonRatingOrder = {
  [HorizonRatings.Extreme]: 0,
  [HorizonRatings.High]: 1,
  [HorizonRatings.Moderate]: 2,
  [HorizonRatings.Low]: 3,
  [HorizonRatings.Minimal]: 4,
  [HorizonRatings.Unrated]: 5
};

export interface HorizonRatingTextAndColor {
  id: string; // HorizonRatings enum or uid of Rating Scale
  text: string;
  color: string;
}

export const getLocationDisplayText = (name: string, countryName: string) => {
  const locationDisplayText = [name];

  if (Boolean(countryName) && name !== countryName) {
    locationDisplayText.push(countryName);
  }

  return locationDisplayText;
};

const registeredLanguages = [];

export const getCountryCode = async (country_id: number, countryName: string, languageCode: string) => {
  const countryCode = HorizonCountryCodeByInternalIdMap[country_id];

  if (Boolean(countryCode)) {
    return countryCode;
  }

  if (!registeredLanguages.includes(languageCode)) {
    try {
      const localeData = (await fetch(`i18n-iso-countries/langs/${languageCode}.json`).then(res => res.json)) as countries.LocaleData;
      countries.registerLocale(localeData);
      registeredLanguages.push(languageCode);
    } catch (err) {
      console.error(err);
    }
  }

  return countries.getAlpha2Code(countryName, languageCode)?.toLowerCase() ?? 'xx';
};

export enum RatingsCategoryRanks {
  Overview = 1,
  GovernmentType = 10,
  Population = 15,
  Languages = 20,
  InternationalCountryCode = 25,
  TimeZones = 30,
  Traffic = 35,
  DrinkingWater = 45,
  Security = 100,
  CivilUnrest = 105,
  Crime = 110,
  Kidnapping = 115,
  Terrorism = 125,
  ArmedConflict = 130,
  SecurityServices = 135,
  Infrastructure = 200,
  GroundTransport = 205,
  LocalBuses = 210,
  MassTransit = 215,
  Taxis = 220,
  TrainTravel = 225,
  VehiclesAndRoads = 230,
  Cyber = 235,
  MaritimeServices = 240,
  AirTravel = 245,
  Utilities = 250,
  Electricity = 255,
  InternetAndTelecommunications = 260,
  Environment = 400,
  ManMadeHazards = 405,
  Seismic = 430,
  StormSystem = 455,
  NaturalHazards = 480,
  AirQuality = 496,
  HealthAndMedical = 500,
  DiseaseAndHealthThreats = 505,
  HealthRequirements = 510,
  HealthRecommendations = 515,
  MedicalFacilitiesAndServices = 520,
  MentalHealth = 525,
  FoodAndWaterSafety = 530,
  Political = 600,
  Corruption = 605,
  GovernmentStability = 610,
  RuleOfLaw = 615,
  Sanctions = 620,
  Legal = 650,
  EmergencyContacts = 655,
  EmbassiesAndConsulates = 660,
  EntryExit = 700,
  PreDeparture = 705,
  OnArrival = 710,
  InTransit = 715,
  AtYourHotel = 720,
  InTheStreet = 725,
  Culture = 800,
  Gestures = 805,
  Religions = 810,
  Ethnicities = 815,
  SignificantDates = 820,
  Currency = 905,
  CreditCard = 910,
  TaxesAndTipping = 915,
  BankAndATM = 920,
  LGBTQSecurity = 1000,
  LGBTQLegalEnvironment = 1005,
  LGBTQEnforcement = 1015,
  LGBTQSocialEnvironment = 1020
}

export const TopLevelCategoryRanks = [
  RatingsCategoryRanks.Security,
  RatingsCategoryRanks.Infrastructure,
  RatingsCategoryRanks.Environment,
  RatingsCategoryRanks.HealthAndMedical,
  RatingsCategoryRanks.Political,
  RatingsCategoryRanks.Legal,
  RatingsCategoryRanks.EntryExit,
  RatingsCategoryRanks.Culture,
  RatingsCategoryRanks.LGBTQSecurity
];

export const CategoryRanksForOverview = [
  RatingsCategoryRanks.Security,
  RatingsCategoryRanks.Infrastructure,
  RatingsCategoryRanks.Environment,
  RatingsCategoryRanks.HealthAndMedical,
  RatingsCategoryRanks.Political,
  RatingsCategoryRanks.LGBTQSecurity
];

export enum RatingsIconType {
  System = 'system',
  SvgComponent = 'svg_component'
}

export interface RatingsSystemIconConfig {
  type: RatingsIconType.System;
  path: string;
}

export interface RatingsSvgComponentIconConfig {
  type: RatingsIconType.SvgComponent;
  component: () => Type<SvgComponent>;
}

export type RatingsCategoryIconConfig = RatingsSystemIconConfig | RatingsSvgComponentIconConfig;

export const RatingsCategoryIconConfigMap: Record<RatingsCategoryRanks[keyof RatingsCategoryRanks & number], RatingsCategoryIconConfig> = {
  [RatingsCategoryRanks.Overview]: {
    type: RatingsIconType.System,
    path: 'icon-map_search'
  },
  [RatingsCategoryRanks.Security]: {
    type: RatingsIconType.System,
    path: 'icon-security_officer'
  },
  [RatingsCategoryRanks.Infrastructure]: {
    type: RatingsIconType.System,
    path: 'icon-road'
  },
  [RatingsCategoryRanks.Environment]: {
    type: RatingsIconType.System,
    path: 'icon-sun_fog'
  },
  [RatingsCategoryRanks.HealthAndMedical]: {
    type: RatingsIconType.System,
    path: 'icon-hospital_cross'
  },
  [RatingsCategoryRanks.Political]: {
    type: RatingsIconType.System,
    path: 'icon-government'
  },
  [RatingsCategoryRanks.Legal]: {
    type: RatingsIconType.System,
    path: 'icon-evidence'
  },
  [RatingsCategoryRanks.EntryExit]: {
    type: RatingsIconType.System,
    path: 'icon-air-flow'
  },
  [RatingsCategoryRanks.Culture]: {
    type: RatingsIconType.System,
    path: 'icon-people-group'
  },
  [RatingsCategoryRanks.LGBTQSecurity]: {
    type: RatingsIconType.SvgComponent,
    component: () => HeartSVGComponent
  }
};

export interface RatingScaleDetails {
  id: string; // is the severity id or the rating scale uid
  name: string;
  color: string;
  order: number;
  documents?: Document[];
}

interface RatingLevelWithRange extends HorizonRatingTextAndColor {
  range: {
    upper: number;
    lower: number;
  };
}

export enum HorizonLocationTiers {
  Country = '6',
  StateProvince = '5',
  City = '3',
  ThreatZones = 'THREAT-ZONES'
}

export const HorizonLocationTierOrder = {
  [HorizonLocationTiers.ThreatZones]: 0,
  [HorizonLocationTiers.City]: 1,
  [HorizonLocationTiers.StateProvince]: 2,
  [HorizonLocationTiers.Country]: 3
};

export interface HorizonRatingsFolderDetails {
  tier: string;
  name: string;
}

const baseTranslationKey = 'MAP.HORIZON-LOCATION-INTEL-RATINGS';

type LayerConfig = {
  translationKey?: string;
  name?: string;
  color: string;
  filters: Filter[];
  filterGroups?: FilterGroup[];
};

// Horizon Rank Rating 1 is the Overview category
const OverviewRatingFilter: Filter = { field: 'rank', operator: RuleOperator.Equal, value: '1' };

const DefaultLayerConfigs: LayerConfig[] = [
  {
    translationKey: 'EXTREME',
    color: '#e03131',
    filters: [{ field: 'rating_number', operator: RuleOperator.GreaterThanEqualTo, value: '4.5' }]
  },
  {
    translationKey: 'HIGH',
    color: '#fd7e14',
    filters: [
      { field: 'rating_number', operator: RuleOperator.LessThanEqualTo, value: '4.25' },
      { field: 'rating_number', operator: RuleOperator.GreaterThanEqualTo, value: '3.5' }
    ]
  },
  {
    translationKey: 'MODERATE',
    color: '#fcc419',
    filters: [
      { field: 'rating_number', operator: RuleOperator.LessThanEqualTo, value: '3.25' },
      { field: 'rating_number', operator: RuleOperator.GreaterThanEqualTo, value: '2.5' }
    ]
  },
  {
    translationKey: 'LOW',
    color: '#82c91e',
    filters: [
      { field: 'rating_number', operator: RuleOperator.LessThanEqualTo, value: '2.25' },
      { field: 'rating_number', operator: RuleOperator.GreaterThanEqualTo, value: '1.5' }
    ]
  },
  {
    translationKey: 'MINIMAL',
    color: '#2b8a3e',
    filters: [
      { field: 'rating_number', operator: RuleOperator.LessThanEqualTo, value: '1.25' },
      { field: 'rating_number', operator: RuleOperator.GreaterThanEqualTo, value: '1' }
    ]
  }
];

const DEFAULT_MAX_RATING_VALUE = 5;
const ABSOLUTE_MAX_RATING_VALUE = 10;

@Injectable({ providedIn: 'root' })
export class HorizonRatingsUtilsService {
  private store = inject(Store);
  private http = inject(HttpService);
  private translate = inject(TranslateService);

  private datasetsForSelect = this.store.selectSignal(selectDatasetsForSelect);

  ratingsDataset = computed(() => {
    const datasets = this.datasetsForSelect() ?? [];
    return datasets.find(
      ({ type, subtype, isActive }) => isActive && type === DatasetType.Alerts && subtype === DatasetSubtype.HorizonLocationIntelRatings
    );
  });

  threatZonesDataset = computed(() => {
    const datasets = this.datasetsForSelect() ?? [];
    return datasets.find(
      ({ type, subtype, isActive }) => isActive && type === DatasetType.Alerts && subtype === DatasetSubtype.HorizonGeospatialThreatZone
    );
  });

  customRatingScale = signal<RatingScale>(null);

  orderedLevels = computed<RatingScaleLevel[]>(() => (this.customRatingScale()?.levels ?? []).sort((a, b) => b.level - a.level));

  maxRatingValue = computed<number>(() => {
    const customRatingScale = this.customRatingScale();

    if (!customRatingScale) {
      return DEFAULT_MAX_RATING_VALUE;
    }

    return customRatingScale?.configuration?.HighestRating ?? ABSOLUTE_MAX_RATING_VALUE;
  });

  ratingScaleRanges = computed<RatingLevelWithRange[]>(() =>
    this.orderedLevels().reduce((acc, { level, uId, label, htmlColorCode, configuration }, index) => {
      acc.push({
        id: uId,
        text: label,
        color: htmlColorCode,
        range: {
          upper: index === 0 ? untracked(this.maxRatingValue) : acc.at(index - 1).range.lower - 0.25,
          lower: configuration?.RatingScale ?? level
        }
      });

      return acc;
    }, [] as RatingLevelWithRange[])
  );

  ratingScaleDetails = computed<RatingScaleDetails[]>(() => {
    if (!!this.customRatingScale()) {
      return this.orderedLevels()
        .reverse()
        .map(({ uId: id, name, htmlColorCode: color, level: order }) => ({
          id,
          name,
          color,
          order
        }));
    }

    return Object.entries(HorizonRatings).map(([key, value]) => ({
      id: value,
      name: this.translate.instant(`${baseTranslationKey}.RATINGS.${key.toUpperCase()}`),
      color: HorizonRatingColors[value],
      order: HorizonRatingOrder[value]
    }));
  });

  initialize({ configuration }: Folder): void {
    this.customRatingScale.set(null);

    const { ratingScaleUId = null } = !!configuration ? JSON.parse(configuration) : {};

    if (!!ratingScaleUId) {
      this.loadCustomRatingScale(ratingScaleUId);
    }
  }

  async getLocationIntelRatingScale(): Promise<RatingScale> {
    return this.http
      .get(`${API_BASE_URL}/v1/meta/ratings/area/${RatingArea.HorizonLocationIntelRatings}`)
      .toPromise()
      .catch(err => {
        console.error(err);
        return null;
      });
  }

  async loadCustomRatingScale(ratingScaleUId: string): Promise<void> {
    const res: RatingScale = await this.http
      .get(`${API_BASE_URL}/v1/meta/ratings/${ratingScaleUId}`)
      .toPromise()
      .catch(err => {
        console.error(err);
        return null;
      });

    this.customRatingScale.set(res);
    return;
  }

  private getRatingsTextAndColor_systemDefault = (value: number | null): HorizonRatingTextAndColor => {
    // Max value is 5 for Horizon Ratings
    let rating: HorizonRatings = HorizonRatings.Unrated;

    if (value >= 4.5) {
      rating = HorizonRatings.Extreme;
    } else if (value <= 4.25 && value >= 3.5) {
      rating = HorizonRatings.High;
    } else if (value <= 3.25 && value >= 2.5) {
      rating = HorizonRatings.Moderate;
    } else if (value <= 2.25 && value >= 1.5) {
      rating = HorizonRatings.Low;
    } else if (value <= 1.5 && value >= 1) {
      rating = HorizonRatings.Minimal;
    }

    return {
      id: rating,
      text: this.translate.instant(`MAP.HORIZON-LOCATION-INTEL-RATINGS.RATINGS.${rating.toUpperCase()}`),
      color: HorizonRatingColors[rating]
    };
  };

  getRatingsTextAndColor(rating_number: number | null): HorizonRatingTextAndColor {
    if (!this.customRatingScale()) {
      return this.getRatingsTextAndColor_systemDefault(rating_number);
    }

    const ranges = this.ratingScaleRanges();

    const { range, ...result } = ranges.find(({ range }) => rating_number >= range.lower && rating_number <= range.upper);

    return result;
  }

  private transformRatingScaleLevelsToLayerConfigs(ratingScale: RatingScale): LayerConfig[] {
    // ensure level order highest to lowest
    const orderedLevels = ratingScale.levels.sort((a, b) => b.level - a.level);

    // this function only is used on LSD section create, so use what is being passed in since the ratings configuration hasn't been set for this utils yet.
    const maxRating = ratingScale?.configuration?.HighestRating ?? ABSOLUTE_MAX_RATING_VALUE;

    const layerConfigs = orderedLevels.map(({ label, htmlColorCode, level, configuration }, index) => {
      const prevLevel = orderedLevels[index - 1];

      const lteValue = (index === 0 ? maxRating : (prevLevel.configuration?.RatingScale ?? prevLevel.level) - 0.25).toString();
      const gteValue = (configuration?.RatingScale ?? level).toString();

      return {
        name: label,
        color: htmlColorCode,
        filters: [
          { field: 'rating_number', operator: RuleOperator.GreaterThanEqualTo, value: gteValue },
          { field: 'rating_number', operator: RuleOperator.LessThanEqualTo, value: lteValue }
        ],
        filterGroups: []
      };
    });

    return [
      ...layerConfigs
    ];
  }

  generateFolderStructure(parentUId: string, ratingScale?: RatingScale): Folder[] {
    const ratingsDataset = this.ratingsDataset();

    if (!parentUId) {
      throw new Error('No Parent Folder UId detected');
    }

    if (!ratingsDataset) {
      throw new Error('No Ratings Dataset Loaded');
    }

    const threatZonesDataset = this.threatZonesDataset();

    const locationTiers = [HorizonLocationTiers.Country, HorizonLocationTiers.StateProvince, HorizonLocationTiers.City];

    if (!!threatZonesDataset) {
      locationTiers.unshift(HorizonLocationTiers.ThreatZones);
    }

    const _layerConfigs = !!ratingScale ? this.transformRatingScaleLevelsToLayerConfigs(ratingScale) : DefaultLayerConfigs;

    return locationTiers.map((locationTier, folderIndex) => {
      const folderUId = uuidv4();

      const isThreatZoneFolder = locationTier === HorizonLocationTiers.ThreatZones;

      const layerConfigs = isThreatZoneFolder ? _layerConfigs.slice(0, -1) : _layerConfigs;

      const relatedDataset = isThreatZoneFolder ? threatZonesDataset : ratingsDataset;

      const layers = layerConfigs.map(({ translationKey, color, filters, filterGroups = [], name }, layerIndex) => ({
        uId: uuidv4(),
        folderUId,
        name: !!ratingScale ? name : this.translate.instant(`${baseTranslationKey}.RATINGS.${translationKey}`),
        sortOrder: layerIndex,
        isActive: true,
        type: LayerTypes.HorizonLocationIntelRatings,
        color,
        iconPath: null,
        datasetUId: relatedDataset?.uId,
        displayOptions: null,
        foreignDatasetDefinition: null,
        rule: {
          root: {
            filters: isThreatZoneFolder
              ? filters
              : [OverviewRatingFilter, { field: 'tier', operator: RuleOperator.Equal, value: locationTier }, ...filters],
            filterGroups,
            groupType: FilterGroupType.And
          }
        },
        markerName: 'pin-teardrop',
        markerSize: 'medium'
      }));

      // this.folder in this context is the section (section is type of folder)
      const folder: Folder = {
        uId: folderUId,
        parentUId,
        name: this.translate.instant(`${baseTranslationKey}.FOLDERS.LOCATION-TIER-${locationTier}`),
        sectionType: SectionType.None,
        sortOrder: folderIndex,
        folders: [],
        layers
      };

      return folder;
    });
  }
}
